import { useCallback, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { exportToBlob } from '@excalidraw/excalidraw'
import { useSession } from '@/store/useSession'
import { readCanvasSceneForClose } from '@/utils/canvasSceneStorage'
import styles from './CanvasClosePage.module.css'

function CloseLayout({
  subheading,
  children,
  footerLink,
}: {
  subheading: string
  children: ReactNode
  footerLink?: { label: string; onClick: () => void }
}) {
  return (
    <main className={styles.page} aria-label="Session close">
      <div className={styles.card}>
        <header className={styles.header}>
          <h1 className={styles.heading}>That&apos;s the session done.</h1>
          <p className={styles.subheading}>{subheading}</p>
        </header>
        <div className={styles.actions}>{children}</div>
        {footerLink && (
          <button type="button" className={styles.textLink} onClick={footerLink.onClick}>
            {footerLink.label}
          </button>
        )}
      </div>
    </main>
  )
}

/** Screen 1 — Save your canvas? */
export function CanvasCloseSavePage() {
  const navigate = useNavigate()

  const goToDestinations = useCallback(() => {
    navigate('/session/canvas/close/where')
  }, [navigate])

  const handleDownload = useCallback(async () => {
    const scene = readCanvasSceneForClose()
    if (scene) {
      const blob = await exportToBlob({
        elements: scene.elements,
        appState: {
          exportBackground: true,
          viewBackgroundColor: scene.viewBackgroundColor,
        } as Parameters<typeof exportToBlob>[0]['appState'],
        files: scene.files,
        mimeType: 'image/png',
      })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'fitutogether-session.png'
      anchor.click()
      URL.revokeObjectURL(url)
    }
    goToDestinations()
  }, [goToDestinations])

  return (
    <CloseLayout subheading="Would you like to save your canvas?">
      <button type="button" className={styles.primaryBtn} onClick={handleDownload}>
        Download canvas
      </button>
      <div className={styles.disabledOption}>
        <button type="button" className={styles.outlineBtn} disabled aria-disabled="true">
          Save to platform
        </button>
        <p className={styles.comingSoon}>Coming soon</p>
      </div>
      <button type="button" className={styles.textLink} onClick={goToDestinations}>
        Skip
      </button>
    </CloseLayout>
  )
}

/** Screen 2 — Where would you like to go? */
export function CanvasCloseWherePage() {
  const navigate = useNavigate()
  const { userRole } = useSession()
  const isClient = userRole === 'client'

  return (
    <CloseLayout
      subheading="Where would you like to go?"
      footerLink={{
        label: 'Back to canvas',
        onClick: () => navigate('/session/canvas'),
      }}
    >
      {isClient ? (
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => navigate('/session/after', { replace: true })}
        >
          Your Space
        </button>
      ) : (
        <>
          <button
            type="button"
            className={styles.outlineBtn}
            onClick={() => navigate('/session/after', { replace: true })}
          >
            Your Space
          </button>
          <button
            type="button"
            className={styles.outlineBtn}
            onClick={() => navigate('/session/notes', { replace: true })}
          >
            Practitioner notes
          </button>
        </>
      )}
    </CloseLayout>
  )
}
